import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import api from 'apis/api';

import { buildQueryParams } from 'utils/helper';

import { useWorkspaceStore } from 'store/workspace.store';

import type { InboxNotificationsListResponse } from 'types/inbox-notifications/InboxNotifications';

export function useGetUnreadInboxNotifications() {
  const { workspace } = useWorkspaceStore();

  return useQuery({
    queryKey: ['notifications/v1/unread', workspace?.id],
    queryFn: async (): Promise<InboxNotificationsListResponse> => {
      const { data } = await api.get<InboxNotificationsListResponse>(
        `/notifications/v1/unread?${buildQueryParams({ organisation_id: workspace?.id })}`
      );
      return data;
    },
    staleTime: 10000, // Fresh for 10s to avoid redundant fetches
    gcTime: 5 * 60 * 1000, // Cache for 5min in background
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30000, // Auto-refresh every 30s
    refetchIntervalInBackground: false,
    retry: 2,
    enabled: !!workspace?.id,
  });
}

export function useGetInboxNotifications(options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;
  const { workspace } = useWorkspaceStore();

  return useQuery({
    queryKey: ['notifications/v1/all', workspace?.id],
    queryFn: async (): Promise<InboxNotificationsListResponse> => {
      const { data } = await api.get<InboxNotificationsListResponse>(
        `/notifications/v1/all?${buildQueryParams({ organisation_id: workspace?.id })}`
      );
      return data;
    },
    staleTime: 10000, // Fresh for 10s to avoid redundant fetches
    gcTime: 5 * 60 * 1000, // Cache for 5min in background
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: 2,
    enabled: enabled && !!workspace?.id,
  });
}

interface MarkNotificationAsReadResponse {
  success: boolean;
  message?: string;
}
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspaceStore();

  return useMutation({
    mutationFn: async (
      notificationId: string
    ): Promise<MarkNotificationAsReadResponse> => {
      const { data } = await api.patch<MarkNotificationAsReadResponse>(
        `/notifications/v1/${notificationId}/read?${buildQueryParams({ organisation_id: workspace?.id })}`
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate and refetch notifications queries to update the UI
      queryClient.invalidateQueries({ queryKey: ['notifications/v1/unread'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/v1/all'] });
    },
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  const { workspace } = useWorkspaceStore();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.patch(
        `/notifications/v1/unread/read?${buildQueryParams({ organisation_id: workspace?.id })}`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications/v1/unread'] });
      queryClient.invalidateQueries({ queryKey: ['notifications/v1/all'] });
    },
  });
}
